import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, MessageSquare, Sparkles, Play, Music, BookOpen, Pause, X, ChevronDown, ChevronUp, Upload, Plus, RefreshCw, Loader2, FileText, Trash2 } from 'lucide-react';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Character Videos
const CHARACTER_VIDEOS = {
  mena: [
    { id: 'mena-video-1', title: 'Mena ORB Introduction', url: 'https://customer-assets.emergentagent.com/job_uplift-mobile/artifacts/ksocpfpw_Untitled%20design%20%281%29.mp4' },
  ],
  katie: [
    { id: 'katie-video-1', title: "Katie's Video", url: 'https://customer-assets.emergentagent.com/job_uplift-app-16/artifacts/wu6ofn95_Katie%27s%20Video.mp4' },
  ],
  dorian: [
    { id: 'dorian-video-1', title: 'Untitled Design', url: 'https://customer-assets.emergentagent.com/job_uplift-mobile/artifacts/19xn5hce_Untitled%20design%20%282%29.mp4' },
  ],
  luke: [
    { id: 'luke-video-1', title: "Luke's Video", url: 'https://customer-assets.emergentagent.com/job_uplift-app-16/artifacts/tl3abqni_Luke%27s%20Video.mp4' },
  ],
  octavio: [
    { id: 'octavio-video-1', title: 'Join the Movement', url: 'https://customer-assets.emergentagent.com/job_uplift-mobile/artifacts/fckr14el_Join%20the%20Movement%20%281%29.mp4' },
  ],
  orb: [],
};

// Animated Sound Wave Component
const SoundWave = ({ isPlaying, color }) => {
  const barHeights = [40, 70, 50, 80, 60];
  
  return (
    <div className="flex items-center justify-center gap-[2px] h-4 w-5">
      {barHeights.map((height, i) => (
        <div
          key={i}
          className={`w-[3px] rounded-full transition-all ${isPlaying ? 'animate-soundwave' : ''}`}
          style={{
            height: isPlaying ? `${height}%` : '30%',
            backgroundColor: isPlaying ? color : '#9ca3af',
            animationDelay: isPlaying ? `${i * 0.1}s` : '0s',
          }}
        />
      ))}
    </div>
  );
};

// Format time as mm:ss
const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// AI Character configurations (same as in ChatPage)
const AI_CHARACTERS = {
  orb: {
    id: 'orb',
    name: 'ORB Ai Assistant',
    shortDesc: 'Friendly, clear, and action-oriented',
    longDesc: 'A friendly, mission‑driven guide that keeps things clear and conversational, helps users collaborate and make decisions quickly, and always nudges toward practical next steps and real‑world action.',
    image: null,
    color: '#586D52',
    fullBio: `ORB Ai Assistant is the core intelligence behind The Uplift platform. Designed to be your first point of contact, ORB embodies the mission and values of Collective Solutions.

**Personality Traits:**
• Friendly and approachable
• Clear and direct communication
• Action-oriented mindset
• Collaborative spirit

**Best For:**
• Getting started with the platform
• Quick questions and guidance
• Understanding processes and workflows
• Making decisions efficiently

ORB is always ready to help you take the next step toward your goals.`
  },
  mena: {
    id: 'mena',
    name: 'Mena ORB',
    shortDesc: 'Strategic, ethical, and future‑focused',
    longDesc: 'A calm, principled guide who speaks with strategic clarity—helping users think ethically, long‑term, and with purpose.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png',
    color: '#8B5CF6',
    fullBio: `Mena ORB represents the strategic and ethical compass of The Uplift. With a calm demeanor and principled approach, Mena helps you navigate complex decisions with clarity and purpose.

**Personality Traits:**
• Strategic thinker
• Ethically grounded
• Future-focused vision
• Principled guidance

**Best For:**
• Long-term planning and strategy
• Ethical considerations and dilemmas
• Career direction and purpose
• Complex decision-making

Mena brings wisdom and foresight to every conversation, helping you build a path aligned with your values.`
  },
  katie: {
    id: 'katie',
    name: 'Katie ORB',
    shortDesc: 'Warm, steady, and deeply supportive',
    longDesc: 'A steady, nurturing presence who responds with warmth, reassurance, and practical wisdom—making users feel supported and grounded.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png',
    color: '#EC4899',
    fullBio: `Katie ORB is the nurturing heart of The Uplift team. With warmth and genuine care, Katie creates a safe space for you to explore your thoughts and feelings while staying grounded in practical solutions.

**Personality Traits:**
• Warm and nurturing
• Steady and reliable
• Deeply supportive
• Practically wise

**Best For:**
• Emotional support and encouragement
• Working through challenges
• Building confidence
• Finding balance in difficult times

Katie is here to remind you that you're capable and supported, no matter what you're facing.`
  },
  luke: {
    id: 'luke',
    name: 'Luke ORB',
    shortDesc: 'Quiet, precise, and trust‑driven',
    longDesc: 'A reserved, trust‑first operator who communicates clearly and minimally, prioritizing safety, verification, and reliability.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png',
    color: '#3B82F6',
    fullBio: `Luke ORB embodies precision and reliability. With a reserved demeanor, Luke focuses on what matters most: getting things right, building trust, and ensuring security in every interaction.

**Personality Traits:**
• Quiet and thoughtful
• Precise communication
• Trust-focused approach
• Reliable and consistent

**Best For:**
• Technical questions and details
• Security and verification matters
• Tasks requiring precision
• Building systematic approaches

Luke speaks only when necessary, but every word carries weight and purpose.`
  },
  dorian: {
    id: 'dorian',
    name: 'Dorian ORB',
    shortDesc: 'Direct, resilient, and action‑oriented',
    longDesc: 'A no‑nonsense, action‑oriented voice that cuts through noise and focuses users on resilience, realism, and next steps.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png',
    color: '#F59E0B',
    fullBio: `Dorian ORB is the voice of resilience and action. When you need someone to cut through the noise and get straight to what matters, Dorian is your guide.

**Personality Traits:**
• Direct and honest
• Resilient mindset
• Action-oriented focus
• Realistic perspective

**Best For:**
• Overcoming obstacles
• Staying motivated during challenges
• Getting unstuck
• Reality checks and honest feedback

Dorian doesn't sugarcoat, but that directness comes from a place of wanting to see you succeed.`
  },
  octavio: {
    id: 'octavio',
    name: 'Octavio ORB',
    shortDesc: 'Creative, expressive, and energizing',
    longDesc: 'An expressive, high‑energy conversationalist who engages through creativity, culture, and emotional momentum.',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png',
    color: '#10B981',
    fullBio: `Octavio ORB brings energy, creativity, and cultural richness to every conversation. If you need inspiration, motivation, or just a boost of positive energy, Octavio is here for it.

**Personality Traits:**
• Creative and expressive
• High-energy presence
• Culturally engaged
• Emotionally intuitive

**Best For:**
• Creative brainstorming
• Finding inspiration
• Boosting motivation and energy
• Exploring new perspectives

Octavio turns every conversation into an opportunity for discovery and growth.`
  },
  call_recorder: {
    id: 'call_recorder',
    name: 'Call Recorder',
    shortDesc: 'Captures, transcribes, and summarises calls',
    longDesc: 'A dedicated AI persona for call recording knowledge — train it with your team scripts, FAQs, brand voice, and review notes. Auto-summary of each call is NOT fed in here; you train this one separately.',
    image: null,
    color: '#DC2626',
    fullBio: `Call Recorder is the persona used to back call-related AI features in the app. Train it with anything you'd normally hand a call coach: scripts, objection handling, escalation playbooks, brand-voice guidelines, post-call review templates, etc.

**Best For:**
• Onboarding scripts
• Objection-handling cheat sheets
• Compliance/disclosure notes
• Voice and tone guidelines

This persona is intentionally NOT auto-fed call summaries — train it explicitly so you control its knowledge.`
  }
};

const STORAGE_KEY = 'orb_selected_character';

// Hoisted to module scope so CharacterProfilePage's frequent re-renders
// (driven by MusicPlayerContext's per-tick currentTime updates) don't
// remount the <img> and cause the character portrait to flicker.
const CharacterAvatar = ({ character }) => {
  if (!character?.image) {
    return (
      <div
        className="w-32 h-32 rounded-2xl flex items-center justify-center mx-auto"
        style={{ backgroundColor: `${character?.color || '#586D52'}20` }}
      >
        <Sparkles className="w-16 h-16" style={{ color: character?.color }} />
      </div>
    );
  }
  return (
    <div
      className="w-32 h-44 rounded-2xl overflow-hidden border-4 mx-auto shadow-lg"
      style={{ borderColor: character.color }}
    >
      <img
        src={character.image}
        alt={character.name}
        className="w-full h-full object-cover object-top"
      />
    </div>
  );
};

// Helper: case-insensitive title match against an array of {title}
const findHardcodedByTitle = (arr, title) =>
  arr.find((x) => (x.title || '').trim().toLowerCase() === (title || '').trim().toLowerCase());

// Admin-only collapsible card body that shows lyrics + AI summary + a Regenerate button.
const AdminLyricsAndSummary = ({ color, meta, isRegen, showLyrics, onRegenerate, onDelete }) => {
  const lyricsStatus = meta?.lyrics_status;
  const summaryStatus = meta?.summary_status;
  const lyrics = meta?.lyrics || '';
  const summary = meta?.summary || '';

  const Pending = ({ label }) => (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      <span>{label}</span>
    </div>
  );

  return (
    <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#1f1f1f] p-3 space-y-3" data-testid="admin-lyrics-summary">
      {showLyrics && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color }}>Lyrics</p>
            {(isRegen || lyricsStatus === 'pending') && <Pending label="Transcribing…" />}
          </div>
          {lyrics ? (
            <pre className="text-xs whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 max-h-48 overflow-y-auto bg-white dark:bg-[#111] rounded-md p-2 border">{lyrics}</pre>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {lyricsStatus === 'failed' ? 'Transcription failed. Try Regenerate.' : 'No lyrics yet — press Regenerate.'}
            </p>
          )}
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isRegen}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1 disabled:opacity-50"
            style={{ backgroundColor: `${color}1A`, color }}
            data-testid="admin-regen-lyrics-btn"
          >
            {isRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regenerate
          </button>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color }}>AI Summary</p>
          {(isRegen || summaryStatus === 'pending') && <Pending label="Summarizing…" />}
        </div>
        {summary ? (
          <p className="text-xs leading-relaxed text-gray-800 dark:text-gray-200 bg-white dark:bg-[#111] rounded-md p-2 border whitespace-pre-wrap">{summary}</p>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            {summaryStatus === 'failed' ? 'Summary failed. Try Regenerate.' : 'No summary yet — press Regenerate.'}
          </p>
        )}
        <div className="flex items-center justify-between mt-2">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isRegen}
            className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1 disabled:opacity-50"
            style={{ backgroundColor: `${color}1A`, color }}
            data-testid="admin-regen-summary-btn"
          >
            {isRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regenerate
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
              data-testid="admin-delete-media-btn"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CharacterProfilePage = () => {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureEnabled, user, api } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeReleaseTab, setActiveReleaseTab] = useState('music');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [closingVideo, setClosingVideo] = useState(false);
  const videoRef = useRef(null);
  const musicSectionRef = useRef(null);

  // Admin-only media (lyrics + AI summary + uploaded items)
  const [adminMedia, setAdminMedia] = useState([]);
  const [expandedCards, setExpandedCards] = useState({}); // { [cardId]: true }
  const [regenerating, setRegenerating] = useState({});  // { [mediaId]: true }
  const [showUploadDialog, setShowUploadDialog] = useState(null); // 'video' | 'music' | null
  const [showStoryDialog, setShowStoryDialog] = useState(false);
  const [uploadDraft, setUploadDraft] = useState({ title: '', source_url: '', file: null });
  const [storyDraft, setStoryDraft] = useState({ title: '', body: '' });
  const [uploadSubmitting, setUploadSubmitting] = useState(false);

  const {
    currentSong,
    isPlaying,
    timeRemaining,
    playSong,
    getCharacterSongs,
    stopPlayback
  } = useMusicPlayer();

  const character = AI_CHARACTERS[characterId];
  const hardcodedSongs = getCharacterSongs(characterId);
  const hardcodedVideos = CHARACTER_VIDEOS[characterId] || [];

  // Fetch admin media (lyrics + summaries) when an admin opens the page
  const fetchAdminMedia = useCallback(async () => {
    if (!isAdmin || !characterId || !api) return;
    try {
      const res = await api.get(`/admin/character/${characterId}/media`);
      setAdminMedia(res.data.items || []);
    } catch (e) {
      // Silent — non-admins or unauthenticated will hit 403
    }
  }, [isAdmin, characterId, api]);

  useEffect(() => { fetchAdminMedia(); }, [fetchAdminMedia]);

  // Helper: find admin meta for a hardcoded item by title match (so existing
  // music & videos pick up lyrics/summary if a regeneration was triggered).
  const findAdminMetaByTitle = (title, mediaType) => {
    return adminMedia.find(
      (m) => m.media_type === mediaType && (m.title || '').trim().toLowerCase() === (title || '').trim().toLowerCase()
    );
  };

  // Merge: stories come ONLY from admin DB. For mena, seed a mock story if empty.
  const adminStories = adminMedia.filter((m) => m.media_type === 'story');
  const adminVideoUploads = adminMedia.filter(
    (m) => m.media_type === 'video' && !findHardcodedByTitle(hardcodedVideos, m.title)
  );
  const adminMusicUploads = adminMedia.filter(
    (m) => m.media_type === 'music' && !findHardcodedByTitle(hardcodedSongs, m.title)
  );
  const songs = [...hardcodedSongs, ...adminMusicUploads.map((m) => ({ id: m.id, title: m.title, subtitle: '', artist: character?.name, url: m.source_url, _adminMediaId: m.id }))];
  const videos = [...hardcodedVideos, ...adminVideoUploads.map((m) => ({ id: m.id, title: m.title, url: m.source_url, _adminMediaId: m.id }))];

  const toggleCard = (id) => setExpandedCards((p) => ({ ...p, [id]: !p[id] }));

  // Ensure an admin-media row exists for a hardcoded item, then trigger regenerate
  const ensureAndRegenerate = async ({ mediaType, title, sourceUrl, body }) => {
    let meta = findAdminMetaByTitle(title, mediaType);
    if (!meta) {
      // Create the admin row first
      try {
        if (mediaType === 'story') {
          const res = await api.post(`/admin/character/${characterId}/stories`, { title, body: body || '' });
          meta = res.data;
        } else {
          const fd = new FormData();
          fd.append('media_type', mediaType);
          fd.append('title', title);
          fd.append('source_url', sourceUrl || '');
          const res = await api.post(`/admin/character/${characterId}/media/upload`, fd);
          meta = res.data;
        }
        setAdminMedia((p) => [meta, ...p]);
      } catch (e) {
        toast.error('Could not start AI run');
        return null;
      }
    }
    return meta;
  };

  const handleRegenerate = async ({ mediaType, title, sourceUrl, body }) => {
    const meta = await ensureAndRegenerate({ mediaType, title, sourceUrl, body });
    if (!meta) return;
    setRegenerating((p) => ({ ...p, [meta.id]: true }));
    try {
      await api.post(`/admin/character/${characterId}/media/${meta.id}/regenerate`);
      // Silent — admin will see the spinner / status change in-place
      // Poll for status
      let tries = 0;
      const tick = async () => {
        tries += 1;
        await fetchAdminMedia();
        const updated = (await api.get(`/admin/character/${characterId}/media`)).data.items.find((m) => m.id === meta.id);
        if (!updated) return;
        const done = (updated.lyrics_status !== 'pending' && updated.summary_status !== 'pending') || tries > 30;
        if (done) {
          setRegenerating((p) => ({ ...p, [meta.id]: false }));
          await fetchAdminMedia();
        } else {
          setTimeout(tick, 2500);
        }
      };
      setTimeout(tick, 2500);
    } catch (e) {
      setRegenerating((p) => ({ ...p, [meta.id]: false }));
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    }
  };

  const handleAdminUpload = async () => {
    if (!showUploadDialog) return;
    if (!uploadDraft.title.trim()) return toast.error('Title is required');
    if (!uploadDraft.file && !uploadDraft.source_url.trim()) return toast.error('Provide a file or a URL');
    setUploadSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('media_type', showUploadDialog);
      fd.append('title', uploadDraft.title.trim());
      if (uploadDraft.source_url.trim()) fd.append('source_url', uploadDraft.source_url.trim());
      if (uploadDraft.file) fd.append('file', uploadDraft.file);
      await api.post(`/admin/character/${characterId}/media/upload`, fd);
      // Silent — the new card will appear with a spinner until the AI finishes.
      setShowUploadDialog(null);
      setUploadDraft({ title: '', source_url: '', file: null });
      await fetchAdminMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
    setUploadSubmitting(false);
  };

  const handleStoryCreate = async () => {
    if (!storyDraft.title.trim() || !storyDraft.body.trim()) {
      return toast.error('Title and body are required');
    }
    setUploadSubmitting(true);
    try {
      await api.post(`/admin/character/${characterId}/stories`, storyDraft);
      toast.success('Story created — AI is generating a summary');
      setShowStoryDialog(false);
      setStoryDraft({ title: '', body: '' });
      await fetchAdminMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
    setUploadSubmitting(false);
  };

  const handleDeleteAdminMedia = async (mediaId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/admin/character/${characterId}/media/${mediaId}`);
      // Silent — the row will disappear from the list.
      await fetchAdminMedia();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Handle URL query parameter to scroll to music section
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('section') === 'music') {
      setActiveReleaseTab('music');
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        if (musicSectionRef.current) {
          musicSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      // Clean up the URL query param
      navigate(`/character/${characterId}`, { replace: true });
    }
  }, [location.search, characterId, navigate]);

  // Map character IDs to their feature toggle ID prefixes
  const characterFeaturePrefix = {
    orb: 'orb_ai_assistant',
    mena: 'orb_mena',
    katie: 'orb_katie',
    luke: 'orb_luke',
    dorian: 'orb_dorian',
    octavio: 'orb_octavio'
  };

  // Helper to check if a section is enabled for this character
  const isSectionEnabled = (section) => {
    const prefix = characterFeaturePrefix[characterId];
    if (!prefix) return true;
    // Special case for orb which only has one toggle
    if (characterId === 'orb') return isFeatureEnabled('orb_ai_assistant');
    return isFeatureEnabled(`${prefix}_${section}`);
  };

  const handlePlaySong = (song) => {
    playSong(song, characterId);
  };

  const handlePlayVideo = (video) => {
    // Stop any music playing
    if (isPlaying) {
      stopPlayback();
    }
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    // Pause first so audio doesn't trail past the animation
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (e) {}
    }
    setClosingVideo(true);
    // Match the CSS transition duration below (300ms)
    setTimeout(() => {
      setSelectedVideo(null);
      setClosingVideo(false);
    }, 300);
  };
  
  if (!character) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Character not found</p>
          <Button onClick={() => navigate('/chat')}>Back to Chat</Button>
        </div>
      </div>
    );
  }

  const handleStartChat = () => {
    localStorage.setItem(STORAGE_KEY, character.id);
    navigate('/chat');
  };

  // CharacterAvatar is hoisted to module scope (see below) so this parent's
  // ~4×/sec re-renders (driven by MusicPlayerContext's currentTime ticks)
  // never remount the <img> tag — which previously caused a visible flicker.

  // Parse the fullBio to render markdown-like formatting
  const renderBio = (bio) => {
    const lines = bio.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <h3 key={idx} className="font-bold text-base mt-4 mb-2 text-foreground">
            {line.replace(/\*\*/g, '')}
          </h3>
        );
      }
      if (line.startsWith('• ')) {
        return (
          <p key={idx} className="text-sm text-muted-foreground ml-4 mb-1">
            {line}
          </p>
        );
      }
      if (line.trim() === '') {
        return <div key={idx} className="h-2" />;
      }
      return (
        <p key={idx} className="text-sm text-muted-foreground leading-relaxed">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000]" data-testid="character-profile-page">
      {/* Header */}
      <div className="p-4">
        <button 
          onClick={() => navigate(-1)} 
          className="w-10 h-10 rounded-full bg-white/80 dark:bg-[#2a2a2a] flex items-center justify-center shadow-sm hover:shadow-md transition-all"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Profile Content */}
      <div className="px-6 pb-32">
        {/* Avatar */}
        <div className="mb-6">
          <CharacterAvatar character={character} />
        </div>

        {/* Name and Short Description */}
        <div className="text-center mb-6">
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: character.color }}
          >
            {character.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {character.shortDesc}
          </p>
        </div>

        {/* Chat Button */}
        <div className="flex justify-center mb-8">
          <Button
            onClick={handleStartChat}
            className="rounded-full px-6 py-2 shadow-md"
            style={{ backgroundColor: character.color }}
            data-testid="start-chat-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Chat with {character.name.split(' ')[0]}
          </Button>
        </div>

        {/* Bio Card - Only show if about section is enabled */}
        {isSectionEnabled('about') && (
        <div className="bg-white/90 dark:bg-[#1a1a1a]/90 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6" data-testid="character-about-section">
          <h2 className="font-bold text-lg mb-4" style={{ color: character.color }}>
            About {character.name.split(' ')[0]}
          </h2>
          <div className="space-y-1">
            {renderBio(character.fullBio)}
          </div>
        </div>
        )}

        {/* My New Releases Section - Only show if releases section is enabled */}
        {isSectionEnabled('releases') && (
        <div 
          ref={musicSectionRef}
          className="bg-white/90 dark:bg-[#1a1a1a]/90 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800" 
          data-testid="character-releases-section"
        >
          <h2 className="font-bold text-lg mb-4" style={{ color: character.color }}>
            My New Releases
          </h2>
          
          {/* Tab Slider - Filter tabs based on feature toggles */}
          <div className="flex rounded-full bg-gray-100 dark:bg-[#2a2a2a] p-1 mb-4">
            {isSectionEnabled('videos') && (
            <button
              onClick={() => setActiveReleaseTab('videos')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-medium transition-all ${
                activeReleaseTab === 'videos' 
                  ? 'bg-white dark:bg-[#3a3a3a] shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={activeReleaseTab === 'videos' ? { color: character.color } : {}}
              data-testid="tab-videos"
            >
              <Play className="w-3.5 h-3.5" />
              My Videos
            </button>
            )}
            {isSectionEnabled('music') && (
            <button
              onClick={() => setActiveReleaseTab('music')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-medium transition-all ${
                activeReleaseTab === 'music' 
                  ? 'bg-white dark:bg-[#3a3a3a] shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={activeReleaseTab === 'music' ? { color: character.color } : {}}
              data-testid="tab-music"
            >
              <Music className="w-3.5 h-3.5" />
              My Music
            </button>
            )}
            {isSectionEnabled('stories') && (
            <button
              onClick={() => setActiveReleaseTab('stories')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-full text-xs font-medium transition-all ${
                activeReleaseTab === 'stories' 
                  ? 'bg-white dark:bg-[#3a3a3a] shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={activeReleaseTab === 'stories' ? { color: character.color } : {}}
              data-testid="tab-stories"
            >
              <BookOpen className="w-3.5 h-3.5" />
              My Stories
            </button>
            )}
          </div>

          {/* Content Area */}
          <div className="min-h-[120px] bg-gray-50 dark:bg-[#252525] rounded-xl overflow-hidden">
            {activeReleaseTab === 'videos' && (
              <>
                {videos.length > 0 ? (
                  <div className="p-2">
                    <div className="space-y-1.5">
                      {videos.map((video) => {
                        const meta = video._adminMediaId
                          ? adminMedia.find((m) => m.id === video._adminMediaId)
                          : findAdminMetaByTitle(video.title, 'video');
                        const cardId = meta?.id || video.id;
                        const isExpanded = !!expandedCards[cardId];
                        const isRegen = !!regenerating[meta?.id];
                        return (
                          <div key={video.id} className="rounded-lg bg-white dark:bg-[#1a1a1a] shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div
                              onClick={() => handlePlayVideo(video)}
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:shadow-md transition-all"
                              data-testid={`video-${video.id}`}
                            >
                              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${character.color}20` }}>
                                <Play className="w-4 h-4 ml-0.5" style={{ color: character.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{video.title}</p>
                                <p className="text-xs text-muted-foreground">By {character.name}</p>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleCard(cardId); }}
                                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                  data-testid={`video-expand-${video.id}`}
                                  aria-label="Show lyrics & summary"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                                </button>
                              )}
                            </div>
                            {isAdmin && isExpanded && (
                              <AdminLyricsAndSummary
                                color={character.color}
                                meta={meta}
                                isRegen={isRegen}
                                showLyrics={true}
                                onRegenerate={() => handleRegenerate({ mediaType: 'video', title: video.title, sourceUrl: video.url })}
                                onDelete={meta && video._adminMediaId ? () => handleDeleteAdminMedia(meta.id) : null}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-center p-4 h-[120px]">
                    <div>
                      <Play className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No videos yet</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Check back soon for new content!</p>
                    </div>
                  </div>
                )}
                {isAdmin && (
                  <div className="px-2 pb-2">
                    <Button
                      onClick={() => { setUploadDraft({ title: '', source_url: '', file: null }); setShowUploadDialog('video'); }}
                      className="w-full mt-2 rounded-lg"
                      style={{ backgroundColor: character.color, color: 'white' }}
                      data-testid="admin-upload-video-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Upload Video
                    </Button>
                  </div>
                )}
              </>
            )}
            {activeReleaseTab === 'music' && (
              <>
                {songs.length > 0 ? (
                  <div className="p-2">
                    <div className="space-y-1.5">
                      {songs.map((song) => {
                        const isSongPlaying = currentSong?.id === song.id && isPlaying;
                        const isSongCurrent = currentSong?.id === song.id;
                        const meta = song._adminMediaId
                          ? adminMedia.find((m) => m.id === song._adminMediaId)
                          : findAdminMetaByTitle(song.title, 'music');
                        const cardId = meta?.id || song.id;
                        const isExpanded = !!expandedCards[cardId];
                        const isRegen = !!regenerating[meta?.id];
                        return (
                          <div key={song.id} className="rounded-lg bg-white dark:bg-[#1a1a1a] shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div
                              onClick={() => handlePlaySong(song)}
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:shadow-md transition-all"
                              data-testid={`song-${song.id}`}
                            >
                              <button className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all" style={{ backgroundColor: isSongCurrent ? character.color : `${character.color}20` }}>
                                {isSongPlaying ? (<Pause className="w-4 h-4 text-white" />) : (<Play className="w-4 h-4 ml-0.5" style={{ color: isSongCurrent ? 'white' : character.color }} />)}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{song.title}</p>
                                {song.subtitle ? (<p className="text-xs text-muted-foreground truncate">{song.subtitle}</p>) : (<p className="text-xs text-muted-foreground">By {character.name}</p>)}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isSongCurrent && (
                                  <span className="text-xs font-mono" style={{ color: character.color }}>
                                    {formatTime(timeRemaining)}
                                  </span>
                                )}
                                <SoundWave isPlaying={isSongPlaying} color={character.color} />
                                {isAdmin && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleCard(cardId); }}
                                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                    data-testid={`song-expand-${song.id}`}
                                    aria-label="Show lyrics & summary"
                                  >
                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                                  </button>
                                )}
                              </div>
                            </div>
                            {isAdmin && isExpanded && (
                              <AdminLyricsAndSummary
                                color={character.color}
                                meta={meta}
                                isRegen={isRegen}
                                showLyrics={true}
                                onRegenerate={() => handleRegenerate({ mediaType: 'music', title: song.title, sourceUrl: song.url })}
                                onDelete={meta && song._adminMediaId ? () => handleDeleteAdminMedia(meta.id) : null}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-center p-4 h-[120px]">
                    <div>
                      <Music className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No music yet</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Check back soon for new releases!</p>
                    </div>
                  </div>
                )}
                {isAdmin && (
                  <div className="px-2 pb-2">
                    <Button
                      onClick={() => { setUploadDraft({ title: '', source_url: '', file: null }); setShowUploadDialog('music'); }}
                      className="w-full mt-2 rounded-lg"
                      style={{ backgroundColor: character.color, color: 'white' }}
                      data-testid="admin-upload-music-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Upload Song
                    </Button>
                  </div>
                )}
              </>
            )}
            {activeReleaseTab === 'stories' && (
              <>
                {adminStories.length > 0 ? (
                  <div className="p-2">
                    <div className="space-y-1.5">
                      {adminStories.map((story) => {
                        const isExpanded = !!expandedCards[story.id];
                        const isRegen = !!regenerating[story.id];
                        return (
                          <div key={story.id} className="rounded-lg bg-white dark:bg-[#1a1a1a] shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div
                              onClick={() => navigate(`/character/${characterId}/story/${story.id}`)}
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:shadow-md transition-all"
                              data-testid={`story-${story.id}`}
                            >
                              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${character.color}20` }}>
                                <BookOpen className="w-4 h-4" style={{ color: character.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{story.title}</p>
                                <p className="text-xs text-muted-foreground truncate">By {character.name}</p>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleCard(story.id); }}
                                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                  data-testid={`story-expand-${story.id}`}
                                  aria-label="Show summary"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                                </button>
                              )}
                            </div>
                            {isAdmin && isExpanded && (
                              <AdminLyricsAndSummary
                                color={character.color}
                                meta={story}
                                isRegen={isRegen}
                                showLyrics={false}
                                onRegenerate={() => handleRegenerate({ mediaType: 'story', title: story.title, body: story.body })}
                                onDelete={() => handleDeleteAdminMedia(story.id)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-center p-4 h-[120px]">
                    <div>
                      <BookOpen className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No stories yet</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Check back soon for new stories!</p>
                    </div>
                  </div>
                )}
                {isAdmin && (
                  <div className="px-2 pb-2">
                    <Button
                      onClick={() => { setStoryDraft({ title: '', body: '' }); setShowStoryDialog(true); }}
                      className="w-full mt-2 rounded-lg"
                      style={{ backgroundColor: character.color, color: 'white' }}
                      data-testid="admin-new-story-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> New Entry
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Upload Dialog (admin only) */}
      <Dialog open={!!showUploadDialog} onOpenChange={(o) => !o && setShowUploadDialog(null)}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Upload {showUploadDialog === 'video' ? 'Video' : 'Song'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Title"
              value={uploadDraft.title}
              onChange={(e) => setUploadDraft((p) => ({ ...p, title: e.target.value }))}
              data-testid="admin-upload-title-input"
            />
            <Input
              placeholder="Or paste a public URL"
              value={uploadDraft.source_url}
              onChange={(e) => setUploadDraft((p) => ({ ...p, source_url: e.target.value }))}
              data-testid="admin-upload-url-input"
            />
            <input
              type="file"
              accept={showUploadDialog === 'video' ? 'video/*' : 'audio/*'}
              onChange={(e) => setUploadDraft((p) => ({ ...p, file: e.target.files?.[0] || null }))}
              className="block w-full text-sm"
              data-testid="admin-upload-file-input"
            />
            <p className="text-xs text-muted-foreground">
              The AI will auto-generate lyrics & a summary, then sync them into {character.name}'s knowledge.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(null)} disabled={uploadSubmitting}>Cancel</Button>
              <Button onClick={handleAdminUpload} disabled={uploadSubmitting} style={{ backgroundColor: character.color, color: 'white' }} data-testid="admin-upload-submit">
                {uploadSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Story Dialog (admin only) */}
      <Dialog open={showStoryDialog} onOpenChange={setShowStoryDialog}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>New Story</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Title"
              value={storyDraft.title}
              onChange={(e) => setStoryDraft((p) => ({ ...p, title: e.target.value }))}
              data-testid="admin-story-title-input"
            />
            <textarea
              placeholder="Write the full story here..."
              value={storyDraft.body}
              onChange={(e) => setStoryDraft((p) => ({ ...p, body: e.target.value }))}
              className="w-full min-h-[260px] rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#586D52]/30"
              data-testid="admin-story-body-input"
            />
            <p className="text-xs text-muted-foreground">
              The AI will auto-generate a summary and add it to {character.name}'s knowledge.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowStoryDialog(false)} disabled={uploadSubmitting}>Cancel</Button>
              <Button onClick={handleStoryCreate} disabled={uploadSubmitting} style={{ backgroundColor: character.color, color: 'white' }} data-testid="admin-story-submit">
                {uploadSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          onClick={handleCloseVideo}
          className={`fixed inset-0 z-[60] bg-black flex items-center justify-center transition-all duration-300 ease-out ${closingVideo ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'}`}
          style={{
            willChange: 'opacity, transform',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <button
            onClick={handleCloseVideo}
            className="absolute z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            style={{
              top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
              right: '16px',
            }}
            data-testid="close-video-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <video
            ref={videoRef}
            src={selectedVideo.url}
            controls
            autoPlay
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full"
            onEnded={handleCloseVideo}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

export default CharacterProfilePage;
